import React, { useState, useEffect } from "react";
import "../profile.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import { NavigationButton } from "@/components/ui/atoms/MNavigationButton";
import { useProfileNavigation } from "@/config/navigation/profile-navigation";
import MLoader from "@/components/ui/atoms/MLoader";
import { getMemberInfo } from "@/hooks/useMembers";
import { MedicalDetailType, MedicalDetailStatus } from "@/types/health-profile";
import { useTranslation } from "react-i18next";
import MDialog from "@/components/ui/atoms/MDialog";
import FormHeader from "@/components/ui/organisms/MHeader";
import { useProfileSummary } from "@/hooks/useHealthProfileSummary";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { HEALTH_PROFILE_URL } from "@/utils/constants";
import MTopBar from "@/components/ui/atoms/MTopBar";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { useNavigationSource } from "@/hooks/useNavigationSource";

const HealthProfile: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { navigateBackIfExists, hasNavigationSource, getNavigationSource } =
    useNavigationSource();
  const [showDialog, setShowDialog] = useState(false);
  const [pendingNav, setPendingNav] = useState<{
    path: string;
    id: string;
    type: MedicalDetailType;
  } | null>(null);

  const { healthProfileNav } = useProfileNavigation();
  const memberInfo = getMemberInfo();

  // Fetch the profile summary
  const { summary, updateNone, loading } = useProfileSummary(memberInfo.id);

  const getType = (id: string) => {
    const values = Object.values(MedicalDetailType);
    return values.find((value) => value === id);
  };

  // Auto-open modal when navigating from ProfileTrackerCard
  useEffect(() => {
    if (!loading && summary && healthProfileNav.length > 0) {
      const openModule = searchParams.get("open");
      console.log("HealthProfile: openModule from query param:", openModule);

      if (openModule) {
        const type = getType(openModule);
        const item = healthProfileNav.find((nav) => nav.id === openModule);

        console.log("HealthProfile: type:", type, "item:", item);

        if (type && item) {
          // Check if we should show the "Do you have?" dialog
          const detail = summary.summary[type];
          const shouldShowDialog = ![
            MedicalDetailType.BLOOD_TYPE,
            MedicalDetailType.PET_CARE,
          ].includes(type);

          console.log(
            "HealthProfile: detail:",
            detail,
            "shouldShowDialog:",
            shouldShowDialog
          );

          if (
            shouldShowDialog &&
            detail?.status === MedicalDetailStatus.NOT_ANSWERED
          ) {
            const targetPath = item.defaultState || item.path;
            console.log("HealthProfile: Opening dialog with path:", targetPath);
            setPendingNav({ path: targetPath, id: item.id, type });
            setShowDialog(true);
          }
        }
      }
    }
  }, [loading, summary, searchParams, healthProfileNav]);

  if (loading || !summary) {
    return (
      <div>
        <MLoader size={40} />
      </div>
    );
  }

  // Helper function to get display value from summary
  const getDisplayFromSummary = (
    type: MedicalDetailType
  ): { message: string; isEmpty: boolean } => {
    if (!summary?.summary)
      return { message: t("common.notAnswered"), isEmpty: true };

    const detail = summary.summary[type];
    if (!detail)
      return {
        message:
          type === MedicalDetailType.NOTE
            ? t("healthProfile.placeholders.noNote")
            : t("common.notAnswered"),
        isEmpty: true,
      };

    if (detail.status === MedicalDetailStatus.NOT_ANSWERED) {
      return { message: t("common.notAnswered"), isEmpty: true };
    } else if (detail.status === MedicalDetailStatus.NONE) {
      return { message: t("healthProfile.placeholders.none"), isEmpty: false };
    } else if (detail.status === MedicalDetailStatus.ANSWERED) {
      if (detail.count && typeof detail.count === "number") {
        return {
          message: t(
            `healthProfile.${type}.count.${
              detail.count === 1 ? "singular" : "plural"
            }`,
            { count: detail.count }
          ),
          isEmpty: false,
        };
      } else if (detail.value) {
        if (type === MedicalDetailType.BLOOD_TYPE) {
          return {
            message: t(`healthProfile.bloodType.${detail.value}`),
            isEmpty: false,
          };
        }
        return {
          message: t(`healthProfile.${type}.value`, { value: detail.value }),
          isEmpty: false,
        };
      } else {
        return { message: t(`healthProfile.${type}.answered`), isEmpty: false };
      }
    }
    return { message: t("common.notAnswered"), isEmpty: true };
  };

  const handleNone = async (type: MedicalDetailType) => {
    try {
      await updateNone(type);
      setShowDialog(false);
      setPendingNav(null);
    } catch (error) {
      showToast({
        message: "Failed to update preference. Please try again.",
        variant: "error",
      });
    }
  };

  const handleItemClick = (item: any) => {
    const type = getType(item.id);
    if (
      [MedicalDetailType.BLOOD_TYPE, MedicalDetailType.PET_CARE].includes(type)
    ) {
      navigate(item.path);
      return;
    }

    const detail = summary.summary[type];
    // Determine if the section has data using summary
    if ([MedicalDetailStatus.ANSWERED].includes(detail?.status)) {
      navigate(item.path);
    } else if ([MedicalDetailStatus.NONE].includes(detail?.status)) {
      navigate(item.defaultState || item.path, {
        state: { backUrl: HEALTH_PROFILE_URL },
      });
    } else {
      // Use defaultState if present
      const targetPath = item.defaultState || item.path;
      setPendingNav({ path: targetPath, id: item.id, type });
      setShowDialog(true);
    }
  };

  const handleBack = () => {
    const didNavigate = navigateBackIfExists();
    if (!didNavigate) {
      navigate("/profile");
    }
  };

  return (
    <div className="profile-page-root">
      <div className="profile-sticky-header">
        <MTopBar
          hideProfilePicker
          hideBack={true}
          onBack={handleBack}
          style={{ background: "#FBF6FF" }}
        />
        <FormHeader
          subHeading="Health profile"
          module="healthProfile"
          memberInfo={memberInfo}
          showProfilePickerRow
          showGradient
          hideBack={false}
          onBack={handleBack}
        />
      </div>
      <div className="profile-content">
        <MSpacer mt={40} />
        <div className="profile-list">
          {healthProfileNav
            .filter((item) => item.id !== "medicine")
            .map((item) => {
              // Get the translated label
              const label = t(item.labelKey);

              const type = getType(item.id);
              // Custom logic for each field using summary data
              const { message, isEmpty } = getDisplayFromSummary(type);

              return (
                <NavigationButton
                  key={item.id}
                  leftIcon={item.iconName}
                  rightIcon="chevron-right"
                  text={label}
                  info={
                    loading ? (
                      "..."
                    ) : isEmpty ? (
                      <span className="profile-not-answered">{message}</span>
                    ) : (
                      message
                    )
                  }
                  onClick={() => handleItemClick(item)}
                />
              );
            })}
        </div>
        {pendingNav && (
          <MDialog
            isOpen={showDialog}
            onClose={() => {
              setShowDialog(false);
              setPendingNav(null);
            }}
            title={t(
              `healthProfile.${pendingNav.type}.none.title${
                memberInfo?.onBehalf ? "_on_behalf" : ""
              }`
            )}
            description={t(`healthProfile.${pendingNav.type}.none.description`)}
            primaryCtaText={t("common.yes")}
            onPrimaryCta={() => {
              setShowDialog(false);
              navigate(pendingNav.path, {
                state: {
                  backUrl: window.location.pathname,
                  openProfileTracker: true,
                },
              });
              setPendingNav(null);
            }}
            secondaryCtaText={t("common.no")}
            onSecondaryCta={() => {
              handleNone(pendingNav.type);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default HealthProfile;
